import { Routes, Route, useLocation, Navigate } from "react-router-dom";

import Home from "./routes/home/home.component";
import Navigation from "./routes/navigation/navigation.component";
import Checkout from "./routes/checkout/checkout.component";
import { useEffect, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";
import { setScheduledTime } from "./store/cart/cart.action";
import {
  selectCartItems,
  selectCartTotal,
  selectRawScheduledTime,
} from "./store/cart/cart.selector";

import { GlobalStyle } from "./global.styles";
import CheckoutConfirmation from "./routes/checkout-confirmation/checkout-confirmation.component_new";
import {
  selectKitchen,
  selectKitchenIsLoading,
  selectOnlineOrdersConfigs,
} from "./store/kitchen/kitchen.selector";
import RedirectPage from "./routes/redirect/redirect.component";
import Payment from "./routes/payment/payment.component";
import ResetPassword from "./routes/authentication/reset-password.component";
import {
  getColor,
  getFontName,
  isOnlineOrderConfigValid,
  sanitizeScheduledTime,
} from "./utils/utils.common";
import { ThemeProvider } from "styled-components";
import { preWarmTyro } from "./utils/tyro/prewarmTyro.utils";
import { httpsCallable } from "firebase/functions";
import { functions } from "./utils/firebase/firebase.utils";
import { roundedUp } from "./utils/roundUpNumber";
import {
  calculateHolidaySurcharge,
  getActiveSurcharge,
} from "./utils/surcharges";
import { resolveDeliveryFeeAmount } from "./utils/deliveryConfig";
import { resolveOrderType } from "./utils/orderType";

const App = () => {
  const dispatch = useDispatch();
  const { pathname, hash, key } = useLocation();
  const kitchen = useSelector(selectKitchen);
  const isKitchenLoading = useSelector(selectKitchenIsLoading);
  const onlineOrdersConfig = useSelector(selectOnlineOrdersConfigs);
  const cartItems = useSelector(selectCartItems);
  const cartSubtotal = useSelector(selectCartTotal);
  const rawScheduledTime = useSelector(selectRawScheduledTime);
  const hasPreWarmedOnCartAddRef = useRef(false);
  const hasPreFetchedOnCartAddRef = useRef(false);

  useEffect(() => {
    const sanitizedScheduledTime = sanitizeScheduledTime(rawScheduledTime);
    if (sanitizedScheduledTime !== rawScheduledTime) {
      dispatch(setScheduledTime(sanitizedScheduledTime));
    }
  }, [dispatch, rawScheduledTime]);
  
  const theme = {
    fontName: getFontName("secondary-font-style", onlineOrdersConfig),
    textColor: getColor("text-body", onlineOrdersConfig),
    bgColor: getColor("header-modal-background", onlineOrdersConfig),
  };

  // Pre-warm Tyro.js on first add to cart or if cart already has items (covers refresh/navigation cases)
  useEffect(() => {
    if (
      cartItems.length > 0 &&
      !hasPreWarmedOnCartAddRef.current &&
      onlineOrdersConfig?.tyroLocationId
    ) {
      hasPreWarmedOnCartAddRef.current = true;
      preWarmTyro();
    }
  }, [cartItems.length, onlineOrdersConfig?.tyroLocationId]);

  // Fire-and-forget pre-fetch of paySecret on first cart add (test for performance improvement)
  useEffect(() => {
    if (
      cartItems.length > 0 &&
      !hasPreFetchedOnCartAddRef.current &&
      onlineOrdersConfig?.tyroLocationId
    ) {
      // Only proceed if we have a valid cartSubtotal (might be 0 initially on mount)
      if (cartSubtotal <= 0) {
        return;
      }
      
      hasPreFetchedOnCartAddRef.current = true;
      
      // Calculate cartTotal with card fees (same logic as checkout)
      const cardFeePercent = onlineOrdersConfig?.cardFeePercent
        ? onlineOrdersConfig.cardFeePercent / 100
        : 0;
      const cardFeeFixedCharge = onlineOrdersConfig?.cardFeeFixedCharge
        ? onlineOrdersConfig.cardFeeFixedCharge
        : 0;
      const activeSurcharge = getActiveSurcharge(onlineOrdersConfig, kitchen?.timezone);
      const holidaySurcharge = roundedUp(
        calculateHolidaySurcharge(cartSubtotal, activeSurcharge)
      );
      const orderType = resolveOrderType(
        new URLSearchParams(window.location.search),
        onlineOrdersConfig
      );
      const deliveryFee =
        orderType === "delivery"
          ? resolveDeliveryFeeAmount(kitchen, cartSubtotal)
          : 0;
      const baseBeforeCardSurcharge = roundedUp(
        Number(cartSubtotal) + Number(holidaySurcharge) + Number(deliveryFee)
      );
      const cardTotalFee = roundedUp(
        baseBeforeCardSurcharge * cardFeePercent + cardFeeFixedCharge
      );
      const cartTotal = roundedUp(
        Number(baseBeforeCardSurcharge) + Number(cardTotalFee)
      );

      // Fire and forget - don't store the result
      const callFunction = httpsCallable(
        functions,
        "tyroEndpoints-createOrder"
      );
      
      callFunction({
        cartTotal: cartTotal,
        tyroLocationID: onlineOrdersConfig.tyroLocationId,
      }).catch((error) => {
        // Silently ignore errors - this is fire and forget
        console.error("Pre-fetch paySecret failed (ignored):", error);
      });
    }
  }, [cartItems.length, onlineOrdersConfig?.tyroLocationId, cartSubtotal]);

  useEffect(() => {
    // if not a hash link, scroll to top
    if (hash === "") {
      window.scrollTo(0, 0);
    }
    // else scroll to id
    else {
      setTimeout(() => {
        const id = hash.replace("#", "");
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView();
        }
      }, 0);
    }
  }, [pathname, hash, key]); // do this on route change
  return (
    <div>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
      </ThemeProvider>
      <Routes>
        <Route path="/:kitchenSlug">
          <Route index element={<Home />} />
          {/* <Route path="auth" element={<Authentication />} /> */}
          <Route
            path="checkout"
            element={
              <>
                {isOnlineOrderConfigValid(onlineOrdersConfig) ? (
                  <>
                    <Navigation title={kitchen?.kitchenName} logoURL={onlineOrdersConfig?.logoURL} />
                    <Checkout />
                  </>
                ) : isKitchenLoading || !kitchen?.kitchenSlug ? null : (
                  <Navigate to={`/${kitchen.kitchenSlug}`} replace />
                )}
              </>
            }
          />
          <Route
            path="checkout/success"
            element={
              <>
                {isOnlineOrderConfigValid(onlineOrdersConfig) ? (
                  <>
                    <Navigation title={kitchen?.kitchenName} logoURL={onlineOrdersConfig?.logoURL} />
                    <CheckoutConfirmation />
                  </>
                ) : isKitchenLoading || !kitchen?.kitchenSlug ? null : (
                  <Navigate to={`/${kitchen.kitchenSlug}`} replace />
                )}
              </>
            }
          >
            {/* <Route path=":session_id" element={<Confirmation />} /> */}
          </Route>

          <Route
            path="checkout/payment"
            element={
              <>
                {isOnlineOrderConfigValid(onlineOrdersConfig) ? (
                  <>
                    <Navigation title={kitchen?.kitchenName} logoURL={onlineOrdersConfig?.logoURL} />
                    <Payment />
                  </>
                ) : isKitchenLoading || !kitchen?.kitchenSlug ? null : (
                  <Navigate to={`/${kitchen.kitchenSlug}`} replace />
                )}
              </>
            }
          />
        </Route>
        <Route
          path="/"
          element={
            <>
              <Navigation />
              <RedirectPage />
            </>
          }
        />
        <Route
          path="/action"
          element={
            <>
              <Navigation />
              <ResetPassword />
            </>
          }
        />
      </Routes>
    </div>
  );
};

export default App;
